import React, { useState } from 'react'
import T from '@/components/Helpers/Translate'
import { cn } from '@/lib/utils'
import { Alert, AlertDescription, AlertTitle } from '../Alert'
import { Button } from '../Button'

interface ErrorPageProps {
  error?: Error
}

export const ErrorPage: React.FC<ErrorPageProps> = ({ error }) => {
  const [showDetails, setShowDetails] = useState(false)

  const handleHomeClick = () => {
    window.location.href = '/'
  }

  const handleRefreshClick = () => {
    window.location.href = '/reset'
  }

  return (
    <div
      className="flex min-h-screen items-center justify-center bg-background p-4"
      role="main"
    >
      <div
        className="w-full max-w-md space-y-6 rounded-xl border border-gray-200/50 bg-white p-6 shadow-lg dark:border-gray-700/30 dark:bg-gray-900"
        aria-live="polite"
      >
        <Alert
          variant="destructive"
          className="border-2 border-destructive/20 bg-destructive/5"
        >
          <AlertTitle
            className="mb-2 text-xl font-bold text-destructive dark:text-red-300"
            role="alert"
          >
            <T>Oops! Something went wrong</T>
          </AlertTitle>
          <AlertDescription className="text-foreground/80 dark:text-gray-300">
            <T>
              We apologize for the inconvenience. Please try refreshing the page
              or contact support if the problem persists.
            </T>
          </AlertDescription>
        </Alert>

        <div className="space-y-4">
          <Button
            variant="default"
            onClick={() => handleRefreshClick()}
            className="w-full"
            aria-label="Refresh Page"
          >
            <T>Refresh Page</T>
          </Button>
          <Button
            variant="outline"
            onClick={handleHomeClick}
            className="w-full"
            aria-label="Go to Homepage"
          >
            <T>Go to Homepage</T>
          </Button>

          {error && (
            <div className="mt-4">
              <Button
                variant="ghost"
                onClick={() => setShowDetails(!showDetails)}
                className="w-full"
                aria-expanded={showDetails}
                aria-controls="error-details"
              >
                {showDetails ? 'Hide Details' : 'Show Details'}
              </Button>

              {showDetails && (
                <div
                  id="error-details"
                  className={cn(
                    'mt-4 max-h-[300px] overflow-auto rounded-lg p-4 text-left',
                    'border border-muted/50 bg-muted/30 text-foreground/80',
                    'focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2',
                  )}
                  tabIndex={0}
                  role="region"
                  aria-label="Error Details"
                >
                  <pre
                    className="whitespace-pre-wrap break-words text-xs"
                    aria-live="polite"
                  >
                    {error.stack || error.message}
                  </pre>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  )
}

// Default props
ErrorPage.defaultProps = {
  error: undefined,
}
